import Tooltip from "@/components/atoms/Tooltip/Tooltip";
import { GAME_ASSET_SLUG } from "@/config/game";
import { cn } from "@/lib/utils";
import {
  getClubsSelector,
  getCountriesSelector,
  getLeaguesSelector,
} from "@/redux/loadedContent/loadedContentSlice";
import { IRootState } from "@/redux/store";
import { useTranslation } from "@repo/shared/i18n";
import { StreamlineSource } from "@repo/shared/types";
import { Check, Copy } from "lucide-react";
import { ReactNode, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { SourceChip } from "./SourceChip";

const assetsBaseUrl = import.meta.env.VITE_APP_ASSETS_BASE_URL;

/* Crests and league marks ship in a light and a dark cut; the panel is dark. */
const crestUrl = (kind: "clubs" | "leagues", id: number) =>
  `${assetsBaseUrl}/${GAME_ASSET_SLUG}/${kind}/dark/${id}.png`;

const flagUrl = (id: number) =>
  `${assetsBaseUrl}/${GAME_ASSET_SLUG}/countries/${id}.png`;

/**
 * One labelled line about a player. `iconUrl` is optional: a fact with no badge
 * of its own indents to line up with the values beside one.
 */
export type PlayerFact = {
  label: string;
  value: string;
  iconUrl?: string;
};

/**
 * Nation, league and club as finished facts, crests included. Ids come from
 * the caller; names come from the loaded-content store, with the caller's own
 * strings as a fallback for a store that has not loaded.
 *
 * The league id may be omitted. It is then looked up by the fallback name
 * before falling back to the club's league: a club id can name the women's
 * side while the player's league says the men's, and the payload's name is
 * the one that matches the player.
 */
export const useCrestFacts = ({
  clubId,
  leagueId,
  countryId,
  fallback,
}: {
  clubId?: number;
  leagueId?: number;
  countryId?: number;
  fallback?: { club?: string; league?: string; nation?: string };
}): PlayerFact[] => {
  const { t } = useTranslation("sbcSolution");
  const club = useSelector(getClubsSelector(clubId ?? -1));
  const leagueByName = useSelector((state: IRootState) =>
    fallback?.league
      ? state.loadedContentReducer.leagues?.find(
          (l) => l.name === fallback.league,
        )
      : undefined,
  );
  const resolvedLeagueId = leagueId ?? leagueByName?.id ?? club?.league;
  const league = useSelector(getLeaguesSelector(resolvedLeagueId ?? -1));
  const country = useSelector(getCountriesSelector(countryId ?? -1));

  const facts: PlayerFact[] = [];
  const nationName = country?.name ?? fallback?.nation;
  if (nationName)
    facts.push({
      label: t("playerInfo.nation"),
      value: nationName,
      iconUrl: countryId !== undefined ? flagUrl(countryId) : undefined,
    });
  const leagueName = league?.name ?? fallback?.league;
  if (leagueName)
    facts.push({
      label: t("playerInfo.league"),
      value: leagueName,
      iconUrl:
        resolvedLeagueId !== undefined
          ? crestUrl("leagues", resolvedLeagueId)
          : undefined,
    });
  const clubName = club?.name ?? fallback?.club;
  if (clubName)
    facts.push({
      label: t("playerInfo.club"),
      value: clubName,
      iconUrl: clubId !== undefined ? crestUrl("clubs", clubId) : undefined,
    });
  return facts;
};

/* How long a copied crest shows its tick before offering to copy again. */
const COPIED_MS = 2000;

/** The card's own copy control, keyed apart from the crest facts. */
const NAME = "name";

/* "Copied!" right beside the control just clicked; the tooltip closes on
   the click, so the tick alone would be the only word of it. */
const CopiedPill = ({ className }: { className?: string }) => {
  const { t } = useTranslation();
  return (
    <span
      className={cn(
        "pointer-events-none absolute left-full z-10 ml-1.5 whitespace-nowrap rounded-md bg-neutral-700 px-1.5 py-0.5 text-[10px] font-bold text-white shadow-md transition-opacity duration-150 starting:opacity-0",
        className,
      )}
    >
      {t("copied")}
    </span>
  );
};

/**
 * Who the player in a slot actually is: the card itself with the crests the
 * card only draws unlabelled stacked beside it. Every one of them is a button
 * that copies a name for a search in the game: the card copies the player's
 * search name, which the card's own label may abbreviate; a crest copies the
 * club, league or nation. Presentational on purpose; each page hands over its
 * own card, search name and finished facts.
 */
export const PlayerFacts = ({
  card,
  source,
  searchName,
  facts,
}: {
  /** The player's card, rendered by the caller at the option cards' size. */
  card: ReactNode;
  /** Where the card comes from: the chip centred under it. */
  source: StreamlineSource;
  /** What to type into the game's search: the common name, else first and last. */
  searchName: string;
  facts: PlayerFact[];
}) => {
  const { t } = useTranslation("sbcSolution");
  /* What was copied last, while its tick shows. */
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    if (copied === null) return;
    const timer = setTimeout(() => setCopied(null), COPIED_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = (key: string, text: string) =>
    navigator.clipboard.writeText(text).then(
      () => setCopied(key),
      (err) => console.error("Failed to copy:", err),
    );

  return (
    /* Full width in the BottomSheet so the names have room, crests after the
       card; on desktop the crests sit before the card and the column is only
       as wide as the crests and their icons. */
    <div className="flex w-full min-w-0 items-center gap-3 touch-device:w-auto touch-device:flex-row-reverse">
      <div className="flex shrink-0 flex-col items-center gap-2">
        {/* Above the popover (z-110) the buttons sit in. */}
        <Tooltip content={searchName} delayDuration={150} className="z-[120]">
          <button
            type="button"
            aria-label={`${t("common:copy")} ${searchName}`}
            onClick={() => copy(NAME, searchName)}
            className="relative block rounded-md transition-[filter] duration-200 hover:brightness-110 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-neutral-500"
          >
            {card}
            {/* Level with the copy badge in the card's corner. */}
            {copied === NAME && <CopiedPill className="bottom-[6%]" />}
            {/* Bottom right, where the swap badge sits on a row card. */}
            <span className="absolute bottom-[6%] right-[6%] flex size-5 items-center justify-center rounded-full border border-panel-foreground bg-gray-800">
              {copied === NAME ? (
                <Check className="size-3 text-streamline-success" />
              ) : (
                <Copy className="size-3 text-white" />
              )}
            </span>
          </button>
        </Tooltip>
        <SourceChip source={source} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col items-start gap-1 touch-device:flex-none">
        {facts.map((fact) => {
          const done = copied === fact.label;
          return (
            <Tooltip
              key={fact.label}
              content={fact.value}
              delayDuration={150}
              className="z-[120]"
            >
              <button
                type="button"
                aria-label={`${t("common:copy")} ${fact.label}: ${fact.value}`}
                onClick={() => copy(fact.label, fact.value)}
                className="relative flex max-w-full items-center gap-1.5 rounded-md p-1 transition-colors hover:bg-neutral-800 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-neutral-500"
              >
                {done && <CopiedPill className="top-1/2 -translate-y-1/2" />}
                {fact.iconUrl && (
                  <img
                    src={fact.iconUrl}
                    alt=""
                    className="size-6 shrink-0 object-contain"
                  />
                )}
                {/* The name is written out in the BottomSheet and truncated
                    before the icon runs out of room; on desktop the crest
                    stands alone, with the name as its tooltip. */}
                <span
                  className={cn(
                    "min-w-0 truncate text-left text-sm text-white",
                    fact.iconUrl
                      ? "touch-device:hidden"
                      : "touch-device:max-w-16",
                  )}
                >
                  {fact.value}
                </span>
                {done ? (
                  <Check className="size-3 shrink-0 text-streamline-success" />
                ) : (
                  <Copy className="size-3 shrink-0 text-neutral-400" />
                )}
              </button>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};
